<template>
  <div class="drivers-behavior-chart rounded-lg border border-gray-200 p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('DRIVERS_BEHAVIOR_ANALYSIS') }}
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t('DRIVER_DISTANCE_VS_RISK_SCATTER') }}
        </p>
      </div>
    </div>

    <!-- Chart -->
    <div class="chart-container">
      <apexchart
        ref="chart"
        v-if="chartOptions && chartSeries.length"
        type="scatter"
        height="320"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="flex items-center justify-center h-64 text-gray-500">
        {{ $t('NO_DATA') }}
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'DriversBehaviorChart',
  components: { apexchart: VueApexCharts },
  props: {
    // expects: [ {driver_id, total_traveled_distance, risk_score, first_name, last_name}, ... ]
    data: { type: Array, default: () => [] },
  },
  data() {
    return {
      hiddenSeries: [], // Array of hidden series names for legend filtering
    }
  },
  computed: {
    chartSeries() {
      if (!this.data || !this.data.length) return []

      // Group data by risk level for different series
      const lowRiskDrivers = []
      const mediumRiskDrivers = []
      const highRiskDrivers = []

      this.data.forEach((driver) => {
        const distanceKm = Math.round(
          (driver.total_traveled_distance || 0) / 1000
        )
        const riskScore = driver.risk_score
        const riskLevel = this.getRiskLevel(driver.risk_score)

        const dataPoint = {
          x: distanceKm, // X-axis: distance
          y: riskScore, // Y-axis: risk score
          driverName: driver.first_name + ' ' + driver.last_name,
          riskLevel: riskLevel,
        }

        if (riskLevel === 'LOW_RISK') {
          lowRiskDrivers.push(dataPoint)
        } else if (riskLevel === 'MEDIUM_RISK') {
          mediumRiskDrivers.push(dataPoint)
        } else {
          highRiskDrivers.push(dataPoint)
        }
      })

      const series = []

      // Always include all series, let the legend handle visibility
      if (lowRiskDrivers.length > 0) {
        series.push({
          name: this.$t('LOW_RISK'),
          data: lowRiskDrivers,
          color: '#10B981', // Green
        })
      }
      if (mediumRiskDrivers.length > 0) {
        series.push({
          name: this.$t('MEDIUM_RISK'),
          data: mediumRiskDrivers,
          color: '#EAB308', // Yellow
        })
      }
      if (highRiskDrivers.length > 0) {
        series.push({
          name: this.$t('HIGH_RISK'),
          data: highRiskDrivers,
          color: '#DC2626', // Red
        })
      }

      return series
    },
    chartOptions() {
      if (!this.data || !this.data.length) return null

      var self = this
      return {
        chart: {
          type: 'scatter',
          height: 320,
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 500 },
          zoom: { enabled: true },
          events: {
            legendClick: function (chartContext, seriesIndex, config) {
              // Prevent default legend behavior
              return false
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          type: 'numeric',
          title: {
            text: this.$t('TRAVELED_DISTANCE') + ' (km)',
            style: { fontSize: '12px', color: '#6B7280' },
          },
          labels: {
            style: { fontSize: '11px', colors: '#6B7280' },
            formatter: function (val) {
              return Math.round(val).toLocaleString('fa-IR') + ' km'
            },
          },
          axisBorder: { show: true },
          axisTicks: { show: true },
        },
        yaxis: {
          title: {
            text: this.$t('RISK_SCORE'),
            style: { fontSize: '12px', color: '#6B7280' },
            offsetX: -40,
            rotate: -90,
          },
          labels: {
            style: { fontSize: '12px', colors: '#6B7280' },
            formatter: function (val) {
              return val.toFixed(1)
            },
            offsetX: -20,
          },
          min: 0,
          max: 100,
        },
        tooltip: {
          shared: false,
          intersect: true,
          custom: function ({ series, seriesIndex, dataPointIndex, w }) {
            const seriesData = w.config.series[seriesIndex].data[dataPointIndex]
            const driverName = seriesData.driverName
            const distanceKm = seriesData.x
            const riskScore = seriesData.y.toFixed(2)
            const riskLevel = seriesData.riskLevel

            return `
              <div class="apexcharts-tooltip-custom" style="padding: 12px; background: white; border: 1px solid #e5e7eb; border-radius: 6px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
                <div style="font-weight: 600; margin-bottom: 8px; color: #374151;">${driverName}</div>
                <div style="margin-bottom: 4px;">
                  <span style="color: #374151;">${self.$t(
                    'TRAVELED_DISTANCE'
                  )}: </span>
                  <span style="font-weight: 600;">${distanceKm.toLocaleString(
                    'fa-IR'
                  )} km</span>
                </div>
                <div style="margin-bottom: 4px;">
                  <span style="color: #374151;">${self.$t(
                    'RISK_SCORE'
                  )}: </span>
                  <span style="font-weight: 600; color: ${self.getRiskColor(
                    seriesData.y
                  )};">${riskScore}</span>
                </div>
                <div>
                  <span style="color: #374151;">${self.$t(
                    'RISK_LEVEL'
                  )}: </span>
                  <span style="font-weight: 600; color: ${self.getRiskColor(
                    seriesData.y
                  )};">${self.$t(riskLevel)}</span>
                </div>
              </div>
            `
          },
        },
        legend: {
          show: true,
          position: 'bottom',
          horizontalAlign: 'center',
          fontSize: '12px',
          fontFamily: 'inherit',
          itemMargin: {
            horizontal: 15,
            vertical: 5,
          },
          markers: {
            width: 12,
            height: 12,
            radius: 2,
          },
          onItemClick: {
            toggleDataSeries: true,
          },
          onItemHover: {
            highlightDataSeries: true,
          },
        },
        grid: {
          borderColor: '#F3F4F6',
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
        },
        colors: ['#10B981', '#EAB308', '#DC2626'], // Green, Yellow, Red for Low, Medium, High risk
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 280,
              },
              xaxis: {
                labels: {
                  rotate: -90,
                },
              },
            },
          },
        ],
      }
    },
  },
  methods: {
    getRiskColor(riskScore) {
      if (riskScore >= 0 && riskScore <= 20) {
        return '#10B981' // Green - Low risk
      } else if (riskScore > 20 && riskScore <= 50) {
        return '#EAB308' // Yellow - Medium risk
      } else {
        return '#DC2626' // Red - High risk
      }
    },
    getRiskLevel(riskScore) {
      if (riskScore >= 0 && riskScore <= 20) {
        return 'LOW_RISK'
      } else if (riskScore > 20 && riskScore <= 50) {
        return 'MEDIUM_RISK'
      } else {
        return 'HIGH_RISK'
      }
    },
  },
}
</script>

<style scoped>
.drivers-behavior-chart {
  min-height: 400px;
}
.chart-container {
  width: 100%;
}
.chart-container >>> .apexcharts-xaxis-label,
.chart-container >>> .apexcharts-yaxis-label {
  font-family: inherit;
}
</style>
